import _ from 'lodash';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Col, Divider, Empty, Form, Row, Typography } from 'antd';

import gridSelector from 'common/components/grid-view/controllers/selectors';
import * as actionsGridView from 'common/components/grid-view/controllers/actions';

import {
  useGetCertificatesByProductIds,
  useUpdateCertificate,
  useValidateCertificate,
} from './hook';
import { CERTIFICATE_ACTIONS, DEFAULT_CERTIFICATE_FORM } from './constants';
import {
  CustomNotification,
  FormCancelButton,
  FormDeleteButton,
  FormSaveButton,
  StyledModal,
  WithLoading,
} from 'common/components';

import CertificateForm from './CertificateForm';
import SelectedProducts from './SelectedProducts';
import { validCertificates } from './utils';
import { useGetProductFullView } from 'hooks';
import { useDebouncedCallback } from 'use-debounce';
import EmptyCertificate from './EmptyCertificate';
import { exportFormDateWithoutTime, formatMDY } from 'utils/formatDate';
import moment from 'moment';

const UpdateCertificateModal = (props) => {
  const [formInstance] = Form.useForm();

  const { id: _id, productId: _productId } = useParams();
  const id = _id || _productId;

  let products = useSelector(
    gridSelector.makeSelectDetailCurrentITemsSelection()
  );

  const dispatch = useDispatch();

  const [certificateList, setCertificateList] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);

  const { certificateActions, setCertificateActions, isFullView } = props;

  const { productFull } = useGetProductFullView({ productId: id });
  const { updateMultipleCertificates } = useUpdateCertificate();

  const handleRemoveProduct = () => {
    setCertificateList((prev) => {
      return prev?.filter(
        (product) => product?.certificate?.linkId !== selectedRowKeys[0]
      );
    });
  };

  const onCancel = () => {
    setCertificateActions(null);
  };

  const handleSelectProduct = (product) => {
    formInstance.setFieldsValue({
      ...(product?.certificate || DEFAULT_CERTIFICATE_FORM),
      certificateExpiryDate: product?.certificate?.certificateExpiryDate
        ? moment(formatMDY(product?.certificate?.certificateExpiryDate))
        : null,
    });
  };

  const handleSubmit = async () => {
    try {
      const params = validCertificates(certificateList);

      updateMultipleCertificates.mutate(params, {
        onSuccess: ({ isSuccess, message, data }) => {
          if (isSuccess) {
            CustomNotification.success('Certificate update successfully');
            setCertificateActions(null);
            if (!isFullView) {
              dispatch(actionsGridView.updateItemsSelection([]));
              dispatch(actionsGridView.updateDetailCurrentItemsSelection([]));
            }
          } else {
            if (data?.lockedProducts?.length > 0) {
              showLockedProducts(data?.lockedProducts);
            } else
              CustomNotification.error(message || 'Failed to update failed.');
          }
        },
      });
    } catch (error) {
      console.error(error);
      CustomNotification.error('Server Error!');
    }
  };

  const onValuesChange = useDebouncedCallback((_, allFields) => {
    const [currentId] = selectedRowKeys;

    setCertificateList((pre) =>
      pre?.map((product) => {
        if (product?.certificate?.linkId === currentId) {
          return {
            ...product,
            certificate: {
              ...allFields,
              linkId: product?.certificate?.linkId,
              certificateExpiryDate: exportFormDateWithoutTime(
                allFields?.certificateExpiryDate
              ),
            },
          };
        }
        return product;
      })
    );
  }, 100);

  const handleSetCertificateList = (productCertificates) => {
    const certificateList = [];

    productCertificates?.forEach(({ certificates, ...restProduct }) => {
      certificates?.forEach((certificate) => {
        certificateList?.push({
          ...restProduct,
          certificate: { ...certificate },
        });
      });
    });

    setCertificateList(certificateList);
  };

  const visible = certificateActions === CERTIFICATE_ACTIONS.UPDATE;
  const productIds = products?.map((item) => item?.id);
  const params = {
    productIds: [...(isFullView ? [productFull?.productId] : productIds)],
  };

  const { handleValidate, showLockedProducts } = useValidateCertificate({
    onSubmit: handleSubmit,
    isEdit: true,
  });

  const { isLoading } = useGetCertificatesByProductIds({
    params,
    onSuccess: handleSetCertificateList,
    enabled: visible,
    staleTime: 0,
  });

  const currentProduct =
    certificateList?.find(
      (product) => product?.certificate?.linkId === selectedRowKeys?.[0]
    ) || {};

  const hasCertificate = certificateList?.length > 0;

  return (
    <StyledModal
      title='Update Certificate'
      visible={visible}
      onCancel={onCancel}
      maskClosable={false}
      width={1280}
      bodyStyle={{ height: 'min(600px, 60vh)' }}
      destroyOnClose
      footer={[
        <FormCancelButton onClick={onCancel} />,
        <FormSaveButton
          onClick={() => {
            handleValidate({
              productCertificates: certificateList,
              certificateType: '',
            });
          }}
          disabled={!hasCertificate}
          loading={updateMultipleCertificates?.isLoading}
        />,
      ]}
    >
      <WithLoading loading={isLoading}>
        <Row style={{ height: '100%' }}>
          {hasCertificate ? (
            <>
              <Col span={14} style={{ height: '100%' }}>
                <Typography.Paragraph strong>
                  Certificate Grid
                </Typography.Paragraph>

                <SelectedProducts
                  loading={isLoading}
                  hiddenDelete={true}
                  productList={certificateList}
                  selectedRowKeys={selectedRowKeys}
                  onSelectProduct={handleSelectProduct}
                  setSelectedRowKeys={setSelectedRowKeys}
                />
              </Col>
              <Divider type='vertical' style={{ height: '100%' }} />
              <Col span={9}>
                <div
                  style={{ display: 'flex', justifyContent: 'space-between' }}
                >
                  <Typography.Paragraph style={{ marginBottom: 40 }} strong>
                    Certificate Information
                  </Typography.Paragraph>
                  {!_.isEmpty(currentProduct) && !isFullView && (
                    <FormDeleteButton onClick={handleRemoveProduct} />
                  )}
                </div>
                {currentProduct?.certificate ? (
                  <CertificateForm
                    form={formInstance}
                    onValuesChange={onValuesChange}
                  />
                ) : (
                  <Empty />
                )}
              </Col>
            </>
          ) : (
            <Col span={24}>
              <EmptyCertificate />
            </Col>
          )}
        </Row>
      </WithLoading>
    </StyledModal>
  );
};

export default UpdateCertificateModal;
